import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, AlertCircle, Eye, Sparkles, Trophy, X } from "lucide-react";
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, dismissNotification } from "../service/NotificationService";
import { ftAccessToken, ftUnreadCount } from "../constants/Constants";

function readCachedUnread() {
    const raw = localStorage.getItem(ftUnreadCount);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

function writeCachedUnread(n) {
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    localStorage.setItem(ftUnreadCount, String(safe));
}

const POLL_INTERVAL_MS = 60_000;

const ICON_MAP = {
    check:    { Icon: CheckCircle2, accent: "#2a7a5e" },
    alert:    { Icon: AlertCircle,  accent: "#b8513a" },
    eye:      { Icon: Eye,          accent: "#5b8fa8" },
    sparkle:  { Icon: Sparkles,     accent: "#c89c5a" },
    trophy:   { Icon: Trophy,       accent: "#c89c5a" },
    bell:     { Icon: Bell,         accent: "#2a7a5e" },
};

function iconMeta(key) {
    return ICON_MAP[key] || ICON_MAP.bell;
}

function formatRelative(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function NotificationsBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnreadState] = useState(readCachedUnread);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const wrapRef = useRef(null);
    // Notifications are per-user: the fetch/poll would 401 → token-refresh →
    // hard-redirect to "/" for a logged-out visitor. Public pages that reuse this
    // header (e.g. the funder portal) render it with no token, so skip the poll
    // and hide the bell entirely.
    const loggedIn = !!localStorage.getItem(ftAccessToken);

    const setUnread = useCallback((value) => {
        setUnreadState(prev => {
            const next = typeof value === "function" ? value(prev) : value;
            writeCachedUnread(next);
            return next;
        });
    }, []);

    const refreshList = useCallback(() => {
        fetchNotifications(20)
            .then(res => {
                setItems(res?.notifications ?? []);
                setUnread(res?.unreadCount ?? 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [setUnread]);

    useEffect(() => {
        if (!loggedIn) return;
        refreshList();
        const interval = setInterval(refreshList, POLL_INTERVAL_MS);
        const onVisibility = () => { if (document.visibilityState === "visible") refreshList(); };
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [refreshList, loggedIn]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const togglePanel = () => {
        setOpen(o => !o);
    };

    const handleClickItem = async (n) => {
        if (!n.read) {
            try { await markNotificationRead(n.id); } catch { /* silent */ }
            setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
            setUnread(c => Math.max(0, c - 1));
        }
        if (n.linkPath) navigate(n.linkPath);
        setOpen(false);
    };

    const handleDismiss = async (e, n) => {
        e.stopPropagation();
        try { await dismissNotification(n.id); } catch { /* silent */ }
        setItems(prev => prev.filter(x => x.id !== n.id));
        if (!n.read) setUnread(c => Math.max(0, c - 1));
    };

    const handleMarkAllRead = async () => {
        if (unread === 0) return;
        try { await markAllNotificationsRead(); } catch { /* silent */ }
        setItems(prev => prev.map(x => ({ ...x, read: true })));
        setUnread(0);
    };

    if (!loggedIn) return null;

    return (
        <div className="bell-wrap" ref={wrapRef}>
            <button
                className="bell-btn"
                onClick={togglePanel}
                aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
                aria-expanded={open}
            >
                <Bell size={18} color="var(--ink-700)" />
                {unread > 0 && (
                    <span className="bell-badge">{unread > 9 ? "9+" : unread}</span>
                )}
            </button>

            {open && (
                <div className="bell-panel" role="dialog" aria-label="Notifications">
                    <div className="bell-panel-header">
                        <p className="bell-panel-title">Notifications</p>
                        {unread > 0 && (
                            <button className="bell-mark-all" onClick={handleMarkAllRead}>
                                Mark all read
                            </button>
                        )}
                    </div>
                    {loading && items.length === 0 ? (
                        <div className="bell-empty">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="bell-empty">
                            <Bell size={20} color="var(--ink-250)" />
                            <p>You're all caught up.</p>
                        </div>
                    ) : (
                        <ul className="bell-list">
                            {items.map(n => {
                                const { Icon, accent } = iconMeta(n.icon);
                                return (
                                    <li
                                        key={n.id}
                                        className={`bell-item${n.read ? "" : " bell-item-unread"}`}
                                        onClick={() => handleClickItem(n)}
                                    >
                                        <span className="bell-item-icon" style={{ background: accent }}>
                                            <Icon size={12} color="white" />
                                        </span>
                                        <div className="bell-item-body">
                                            {n.title && <p className="bell-item-headline">{n.title}</p>}
                                            {n.message && <p className="bell-item-text">{n.message}</p>}
                                            <p className="bell-item-time">{formatRelative(n.createdDate)}</p>
                                        </div>
                                        <button
                                            className="bell-item-dismiss"
                                            onClick={(e) => handleDismiss(e, n)}
                                            aria-label="Dismiss"
                                        >
                                            <X size={13} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
